"use strict";

exports.__esModule = true;
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _clsx = _interopRequireDefault(require("clsx"));
var _Checkbox = _interopRequireDefault(require("./Checkbox"));
var _useFormElementValidation = _interopRequireDefault(require("../../hooks/useFormElementValidation"));
var _useRandomId = _interopRequireDefault(require("../../hooks/useRandomId"));
var _useLabel = _interopRequireDefault(require("../../hooks/useLabel"));
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
/**
 * The CheckboxGroup component offers a convenient way to display multiple Checkbox components
 */
const CheckboxGroup = props => {
  const {
    useGroupControl,
    options,
    checked,
    id: controlledId,
    name: controlledName,
    label: initialLabel,
    helper,
    inline,
    displayAsButton,
    shape,
    checkboxesClassName,
    checkboxClassName,
    size,
    color,
    outline,
    required,
    atLeast,
    atMost,
    align,
    onChange,
    onError,
    className,
    disabled
  } = props;
  const checkboxGroupRef = _react.default.useRef();
  const id = _react.default.useRef((0, _useRandomId.default)(controlledId)).current;
  const name = controlledName || id;
  const [stateChecked, setChecked] = _react.default.useState(checked || []);
  const [error, setError] = _react.default.useState(false);
  const isInbetween = checked && options && checked.length && checked.length < options.length;
  const isAll = checked && options && checked.length === options.length;
  const [isIndeterminate, setIsIndeterminate] = _react.default.useState(isInbetween);
  const [isAllChecked, setIsAllChecked] = _react.default.useState(isAll);
  const [errors, methods] = (0, _useFormElementValidation.default)(stateChecked, {
    required,
    atLeast,
    atMost
  });
  const {
    label
  } = (0, _useLabel.default)(initialLabel, {
    ...props,
    id
  });
  _react.default.useEffect(() => {
    const isInbetween = checked && options && checked.length && checked.length < options.length || options.filter(o => o.checked).length > 0;
    const isAll = checked && options && checked.length === options.length;
    setChecked(checked || []);
    setIsIndeterminate(isInbetween);
    setIsAllChecked(isAll);
  }, [checked, options]);
  _react.default.useEffect(() => {
    setError(errors.length > 0);
    if (typeof onError === 'function') {
      onError({
        name,
        errors: errors.length ? errors : []
      });
    }
  }, [errors, onError, name]);
  const handleChange = (checkedCheckbox, ref, event) => {
    if (useGroupControl) {
      const checkboxes = checkboxGroupRef.current.querySelectorAll("input");
      const checkall = checkboxGroupRef.current.querySelector("#" + id + "-checkall");
      var checkedCount = checkboxGroupRef.current.querySelectorAll("input:checked").length;
      checkall.checked = checkedCount > 0;
      checkall.indeterminate = checkedCount > 0 && checkedCount < checkboxes.length;
      setIsIndeterminate(checkall.indeterminate);
      setIsAllChecked(checkedCount === checkboxes.length);
    }
    let newStateChecked = [...stateChecked];
    const {
      id: checkboxId
    } = event.target;
    if (checkedCheckbox) {
      newStateChecked.push(checkboxId);
    } else {
      newStateChecked = stateChecked.filter(value => value !== checkboxId);
    }
    setChecked(newStateChecked);
    methods.onChangeCheckboxGroup(newStateChecked);
    if (typeof onChange === 'function') {
      onChange(newStateChecked);
    }
  };
  const handleChangeAll = (checkedCheckbox, ref, event) => {
    setIsIndeterminate(false);
    setIsAllChecked(checkedCheckbox);
    const checkboxes = checkboxGroupRef.current.querySelectorAll("input");
    const checkall = checkboxGroupRef.current.querySelector("#" + id + "-checkall");
    checkall.checked = checkedCheckbox;
    checkall.indeterminate = false;
    let newStateChecked = [];
    if (checkedCheckbox) {
      for (var i = 0; i < checkboxes.length; i++) {
        const {
          id: checkboxId
        } = checkboxes[i];
        newStateChecked.push(checkboxId);
      }
    }
    for (var x = 0; x < checkboxes.length; x++) {
      checkboxes[x].checked = checkedCheckbox;
    }
    setChecked(newStateChecked);
    methods.onChangeCheckboxGroup(newStateChecked);
    if (typeof onChange === 'function') {
      onChange(newStateChecked, name, event);
    }
  };
  const handleBlur = () => methods.onChangeCheckboxGroup(stateChecked);
  const classes = (0, _clsx.default)('uk-form-item', 'uk-checkbox-group', displayAsButton && 'uk-checkbox-group--display-buttons', useGroupControl && 'uk-checkbox-group-use-control', (helper.length > 0 || error) && 'has-helper', error && 'has-error', inline && 'uk-checkbox-group--inline', disabled && 'disabled', className);
  return /*#__PURE__*/_react.default.createElement("div", {
    className: classes,
    ref: checkboxGroupRef
  }, useGroupControl ? /*#__PURE__*/_react.default.createElement(_Checkbox.default, {
    className: "uk-checkbox-group__group-control",
    align: align,
    checked: isAllChecked,
    isIndeterminate: isIndeterminate,
    id: id + "-checkall",
    name: id + "-checkall",
    onChange: handleChangeAll,
    color: color,
    disabled: disabled,
    size: size,
    outline: outline,
    displayAsButton: displayAsButton,
    label: label,
    "aria-describedby": (name || id) + "-helper-text"
  }) : label && /*#__PURE__*/_react.default.createElement("h3", {
    className: "uk-checkbox-group__label",
    id: id + "-checkall",
    "aria-describedby": (name || id) + "-helper-text"
  }, label), /*#__PURE__*/_react.default.createElement("div", {
    className: checkboxesClassName + " uk-checkbox-group__checkboxes",
    role: "group",
    "aria-labelledby": id + "-checkall"
  }, options.map((item, i) => /*#__PURE__*/_react.default.createElement(_Checkbox.default, {
    align: align,
    key: i,
    name: item.name || name + "_" + i,
    id: item.id || id + "_" + i,
    label: item.label,
    checked: stateChecked.includes(item.id) || stateChecked.includes(id + "_" + i) || item.checked,
    disabled: item.disabled || disabled || false,
    onChange: handleChange,
    onBlur: handleBlur,
    color: color,
    size: size,
    outline: outline,
    value: item.value,
    className: checkboxClassName,
    displayAsButton: displayAsButton,
    shape: shape,
    inputClassName: useGroupControl ? id : ''
  }))), (helper.length > 0 || error) && /*#__PURE__*/_react.default.createElement("small", {
    className: "uk-form-item__helper",
    id: (name || id) + "-helper-text"
  }, error ? errors : helper));
};
CheckboxGroup.propTypes = {
  /**
   * Use a checkbox in label section that manages all the checkboxes (using state indeterminate)
   */
  useGroupControl: _propTypes.default.bool,
  /**
   * Array of Checkboxes props
   */
  options: _propTypes.default.arrayOf(_propTypes.default.shape),
  /**
   * Ids of the Checkboxes to set as selected
   */
  checked: _propTypes.default.arrayOf(_propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.number])),
  /**
   * Prefix for the Checkboxes id (unless it has been set in the options prop)
   */
  id: _propTypes.default.string,
  /**
   * Prefix for the Checkboxes name (unless it has been set in the options prop)
   */
  name: _propTypes.default.string,
  /**
   * Label of the CheckboxGroup
   */
  label: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.element]),
  /**
   * Layout of the CheckboxGroup
   */
  inline: _propTypes.default.bool,
  /**
   * Change the display of the CheckboxGroup
   */
  displayAsButton: _propTypes.default.bool,
  /**
   * Shape of the Checkbox Buttons if 'displayAsButton' is enabled
   */
  shape: _propTypes.default.oneOf(['default', 'round']),
  /**
   * ClassName to apply to each Checkbox
   */
  checkboxClassName: _propTypes.default.string,
  /**
   * ClassName to apply to checkboxes list
   */
  checkboxesClassName: _propTypes.default.string,
  /**
   * Size of the Checkboxes
   */
  size: _propTypes.default.oneOf(['tiny', 'small', 'default', 'large', 'big']),
  /**
   * Background-color of selected Checkboxes (or checkmark color if outline)
   */
  color: _propTypes.default.oneOf(['default', 'primary', 'secondary', 'success', 'alert', 'warning', 'white']),
  /**
   * Invert styling colors of the Checkboxes
   */
  outline: _propTypes.default.bool,
  /**
   * Helper text, is replaced by a message in case of error
   */
  helper: _propTypes.default.string,
  /**
   * Form-validation: set the CheckboxGroup as required
   */
  required: _propTypes.default.bool,
  /**
   * Form-validation: set the minimal number of Checkboxes that must be checked
   */
  atLeast: _propTypes.default.number,
  /**
   * Form-validation: set the maximal number of Checkboxes that can be checked
   */
  atMost: _propTypes.default.number,
  /**
   * Position of the Checkbox relative to its label
   */
  align: _propTypes.default.oneOf(['left', 'right']),
  /**
   * Callback function onChange
   */
  onChange: _propTypes.default.func,
  /**
   * Callback function onError
   */
  onError: _propTypes.default.func,
  /**
   * Set the CheckboxGroup as disabled
   */
  disabled: _propTypes.default.bool
};
CheckboxGroup.defaultProps = {
  useGroupControl: false,
  size: 'default',
  color: 'primary',
  shape: 'default',
  outline: false,
  required: false,
  helper: '',
  checkboxesClassName: '',
  checkboxClassName: '',
  displayAsButton: false,
  align: 'left',
  disabled: false
};
var _default = exports.default = CheckboxGroup;